// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// components
import ShortcutsDialog from 'components/shared/ShortcutProvider/components/ShortcutDialog/ShortcutDialog';

// constants
import {
  DOUBLE_FIRE_DELAY_IN_MS,
  GLOBAL_CONTEXT
} from './ShortcutProvider.consts';

// context
import { ShortcutContext } from './ShortcutContext';

// hooks
import { useTranslation } from 'react-i18next';

// types
import {
  ShortcutAction,
  ShortcutActivatorKey,
  ShortcutContextProps,
  ShortcutControllerKey,
  ShortcutKeySet
} from './ShortcutProvider.types';

// utils
import {
  isTargetInMuiPopoper,
  isValidActivatorKey
} from './ShortcutProvider.utils';
import { isPrimaryModifier } from '../Typography/Kbd/Kbd.utils';
import { isEqual } from 'lodash';

export const ShortcutProvider = ({ children }) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const shortcuts = useRef<ShortcutContextProps['shortcuts']>({
    [GLOBAL_CONTEXT]: {
      isActive: true,
      name: 'GLOBAL',
      shortcuts: {
        info: {
          active: true,
          callback: () => setShowShortcutInfo(true),
          name: 'info',
          label: t('shortcuts.info.label'),
          description: t('shortcuts.info.description'),
          activatorKey: '?',
          controllerKeys: ['SHIFT']
        }
      }
    }
  });

  // Persisted event listener references so they can be removed properly
  const keyDownListeners = useRef<
    Record<string, (event: KeyboardEvent) => void>
  >({});
  const keyUpListeners = useRef<Record<string, (event: KeyboardEvent) => void>>(
    {}
  );

  const [showShortcutInfo, setShowShortcutInfo] = useState<boolean>(false);

  const getActiveShortcuts = useCallback((): Record<string, ShortcutAction> => {
    return Object.values(shortcuts.current)
      .filter(({ isActive }) => isActive)
      .reduce(
        (acc, context) => ({
          ...acc,
          ...context.shortcuts
        }),
        {}
      );
  }, []);

  const createShortcutContext = useCallback((context: string) => {
    if (!context) return;

    shortcuts.current[context] = shortcuts.current[context]
      ? {
          ...shortcuts.current[context],
          isActive: true
        }
      : {
          name: context,
          isActive: true,
          shortcuts: {}
        };
  }, []);

  const setActiveShortcutContexts = useCallback((contexts: string[]) => {
    Object.keys(shortcuts.current).forEach((contextName) => {
      if (!shortcuts.current[contextName]) return;

      shortcuts.current[contextName] = {
        ...shortcuts.current[contextName],
        isActive: contexts.indexOf(contextName) !== -1
      };
    });
  }, []);

  const extractKeySet = (event: KeyboardEvent): ShortcutKeySet | undefined => {
    // this occasionally happens when a user clicks immediately
    // after a reload. Its not clear why a click triggers a
    // keyboard event, but this guard prevents further issues.
    if (!event.key) return;

    const activatorKey = event.key.toUpperCase() as ShortcutActivatorKey;

    if (!isValidActivatorKey(activatorKey)) return;

    const controllerKeys = [
      ...(event.shiftKey ? ['SHIFT'] : []),
      ...(isPrimaryModifier(event) ? ['PRIMARY'] : [])
    ] as ShortcutControllerKey[];

    return { activatorKey, controllerKeys };
  };

  const getShortcutForKeySet = useCallback(
    (set: ShortcutKeySet) => {
      const shortcutMatches = Object.values(getActiveShortcuts()).filter(
        ({ active, activatorKey, controllerKeys }) =>
          active &&
          activatorKey === set.activatorKey &&
          isEqual(
            controllerKeys.slice().sort(),
            set.controllerKeys.slice().sort()
          )
      );

      return shortcutMatches[0] || undefined;
    },
    [getActiveShortcuts]
  );

  const processing = useRef(false);

  const handleShortcutKeyDownEvent = useCallback(
    (event: KeyboardEvent) => {
      if (processing.current) return;

      const target = event.target as HTMLElement;
      if (!target?.isConnected) return;

      if (isTargetInMuiPopoper(target)) return;

      const keySet = extractKeySet(event);
      if (!keySet) return;

      // Check if user is typing in an input field
      const tagName = target.tagName.toUpperCase();
      const isInputElement =
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Determine if this is a non-modifier shortcut
      // Non-modifier shortcuts: plain keys or Shift+key combinations.
      // Shift-only shortcuts are excluded because Shift is primarily for typing
      // uppercase/special characters (e.g., '?', '!') rather than shortcuts.
      const isNonModifierShortcut =
        keySet.controllerKeys.length === 0 ||
        (keySet.controllerKeys.length === 1 &&
          keySet.controllerKeys[0] === 'SHIFT');

      // Block non-modifier shortcuts when typing in input fields
      if (isInputElement && isNonModifierShortcut) return;

      const shortcut = getShortcutForKeySet(keySet);
      if (!shortcut?.active) return;

      processing.current = true;

      event.preventDefault();
      event.stopPropagation();

      shortcut.callback();

      setTimeout(() => (processing.current = false), DOUBLE_FIRE_DELAY_IN_MS);
    },
    [getShortcutForKeySet]
  );

  const enableOrDisableShortcut = useCallback(
    (isEnabled: boolean = true, context: string, name?: string) => {
      if (!shortcuts.current[context]) return;

      if (name) {
        if (!shortcuts.current[context].shortcuts[name]) return;
        shortcuts.current[context].shortcuts[name].active = isEnabled;
        return;
      }

      Object.keys(shortcuts.current[context].shortcuts).forEach(
        (shortcutName) => {
          shortcuts.current[context].shortcuts[shortcutName].active = isEnabled;
        }
      );
    },
    []
  );

  const clearListenersByNames = useCallback((names: string[]) => {
    const keyDownAndUplistenersMap = {
      keyup: keyUpListeners.current,
      keydown: keyDownListeners.current
    };

    Object.entries(keyDownAndUplistenersMap).forEach(
      ([eventKey, listenerSet]) =>
        names.forEach((name) => {
          const listener = listenerSet[name] as EventListener;
          window.removeEventListener(
            eventKey as keyof WindowEventMap,
            listener
          );
          delete listenerSet[name];
        })
    );
  }, []);

  const register = useCallback(
    ({
      context,
      active = true,
      name,
      label,
      callback,
      description = '',
      activatorKey,
      controllerKeys
    }: ShortcutAction & { context: string }) => {
      if (!context) return;

      shortcuts.current[context] = {
        ...shortcuts.current[context],
        shortcuts: {
          ...shortcuts.current[context].shortcuts,
          [name]: {
            active,
            callback,
            name,
            label,
            description,
            activatorKey,
            controllerKeys
          }
        }
      };

      if (!keyDownListeners.current[name]) {
        keyDownListeners.current[name] = (event: KeyboardEvent) =>
          handleShortcutKeyDownEvent(event);
        window.addEventListener('keydown', keyDownListeners.current[name]);
      }
    },
    [handleShortcutKeyDownEvent]
  );

  const unregister = useCallback(
    (context: string, name?: string) => {
      if (!shortcuts.current?.[context]) return;

      if (name) {
        clearListenersByNames([name]);
        return;
      }

      // Remove all shortcuts in context
      const contextShortcutNames = Object.keys(
        shortcuts.current[context].shortcuts
      );
      clearListenersByNames(contextShortcutNames);
      delete shortcuts.current[context];
    },
    [clearListenersByNames]
  );

  useEffect(() => {
    const shortcutsSnapshot = shortcuts.current; // capture ref value

    // Register global shortcuts
    Object.keys(shortcutsSnapshot[GLOBAL_CONTEXT].shortcuts).forEach((name) => {
      if (!keyDownListeners.current[name]) {
        keyDownListeners.current[name] = (event: KeyboardEvent) =>
          handleShortcutKeyDownEvent(event);
        window.addEventListener('keydown', keyDownListeners.current[name]);
      }
    });

    return () => {
      // Clean up all registered shortcut listeners using the same snapshot
      const shortcutNames = Object.values(shortcutsSnapshot).flatMap(
        ({ shortcuts }) => Object.values(shortcuts).map(({ name }) => name)
      );
      clearListenersByNames(shortcutNames);
    };
  }, [clearListenersByNames, handleShortcutKeyDownEvent]);

  const contextValue = useMemo(
    () => ({
      createShortcutContext,
      disableShortcut: (context: string, name?: string) =>
        enableOrDisableShortcut(false, context, name),
      enableShortcut: (context: string, name?: string) =>
        enableOrDisableShortcut(true, context, name),
      setActiveShortcutContexts,
      register,
      unregister,
      shortcuts: shortcuts.current
    }),
    [
      createShortcutContext,
      enableOrDisableShortcut,
      setActiveShortcutContexts,
      register,
      unregister
    ]
  );

  return (
    <ShortcutContext.Provider value={contextValue}>
      {showShortcutInfo && (
        <ShortcutsDialog
          onClose={() => setShowShortcutInfo(false)}
          shortcuts={{
            // Hard-coded global shortcuts
            ...Object.values(shortcuts.current[GLOBAL_CONTEXT].shortcuts),

            // Shortcuts for the active contexts
            ...Object.values(getActiveShortcuts())
          }}
        />
      )}

      {children}
    </ShortcutContext.Provider>
  );
};
